import { Box, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Button, TextField, Typography } from "@platform/design-system";
import {
  type BenefitTypeOption,
  ChildAgeBandsField,
} from "@platform/plan-grid-core";
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";

import { CUSTOM_TEXT_INPUT_SX, CUSTOM_TEXT_RADIO_SX } from "./formatters";
import { computePopoverTopToFit } from "./popoverViewportFit";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";
import {
  type ChildAgeBandsEditorProps,
  useChildAgeBandsEditor,
} from "./useChildAgeBandsEditor";

export type {
  ChildAgeBandsCellEditorParams,
  ChildAgeBandsEditorProps,
} from "./useChildAgeBandsEditor";

type Props = ChildAgeBandsEditorProps & { typeOptions: BenefitTypeOption[] };

const POPOVER_WIDTH = 460;

// The popover grows with its content up to this height, then its content
// scrolls; a CSS viewport unit so it tracks window resizes without a re-render.
const POPOVER_MAX_HEIGHT = "80vh";
// Keep the grown popover clear of the very bottom edge of the screen.
const BOTTOM_MARGIN = 16;

/**
 * AG Grid popup editor for the Present "Benefit Description & Ages - CH" cell.
 * Edits the `childBenefitAmountsByAge` array: any number of age bands, each with
 * its own lower/upper age-type (unit) and limit plus a benefit type and amount.
 * State and persistence live in useChildAgeBandsEditor.
 */
export const ChildBenefitAgeBandsCellEditor = forwardRef<unknown, Props>(
  function ChildBenefitAgeBandsCellEditor(props, ref) {
    const editor = useChildAgeBandsEditor(props);

    useImperativeHandle(ref, () => ({
      getValue: editor.getValue,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const { editingCustomText } = editor;

    const containerRef = useRef<HTMLDivElement | null>(null);
    usePopupEditorKeyboard(
      containerRef,
      editor.handleSave,
      editor.handleCancel,
    );

    // AG Grid anchors the popover to the cell using its height at editor-start
    // and never re-places it, so a popover that grows downward (bands added, or
    // already-many bands on open) runs off the bottom of a lower cell. Watch it
    // for size changes and slide AG Grid's popup wrapper up by any overflow so
    // it stays fully visible — the one place we touch AG Grid's own element.
    useLayoutEffect(() => {
      const card = containerRef.current;
      const wrapper = card?.closest<HTMLElement>(".ag-popup-editor");
      if (!card || !wrapper) return;

      const keepPopoverOnScreen = (): void => {
        const currentTop = wrapper.style.top
          ? parseFloat(wrapper.style.top)
          : wrapper.getBoundingClientRect().top;
        const nextTop = computePopoverTopToFit({
          popoverBottom: card.getBoundingClientRect().bottom,
          currentTop,
          viewportHeight: window.innerHeight,
          margin: BOTTOM_MARGIN,
        });
        if (nextTop !== null) wrapper.style.top = `${nextTop}px`;
      };

      keepPopoverOnScreen();
      const observer = new ResizeObserver(keepPopoverOnScreen);
      observer.observe(card);
      return () => observer.disconnect();
    }, []);

    // A new band appends to the bottom of the scroll area, so bring it into
    // view once it has rendered — nearest so an already-visible band doesn't
    // jump.
    const bandsRef = useRef<HTMLDivElement | null>(null);
    const handleAddBand = (): void => {
      editor.addBand();
      requestAnimationFrame(() =>
        bandsRef.current?.lastElementChild?.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        }),
      );
    };

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: POPOVER_WIDTH,
          maxHeight: POPOVER_MAX_HEIGHT,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack
          ref={bandsRef}
          spacing={1.5}
          sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}
        >
          <Typography variant="subtitle1">
            <strong>Edit Benefit - Description &amp; Ages - CH</strong>
          </Typography>

          {editor.customTextSupported && (
            <RadioGroup
              row
              value={editor.editMode}
              onChange={(event) =>
                editor.setEditMode(event.target.value as typeof editor.editMode)
              }
            >
              <FormControlLabel
                value="benefit"
                control={<Radio size="small" sx={CUSTOM_TEXT_RADIO_SX} />}
                label="Edit Benefit"
              />
              <FormControlLabel
                value="customText"
                control={<Radio size="small" sx={CUSTOM_TEXT_RADIO_SX} />}
                label="Enter Custom Text"
              />
            </RadioGroup>
          )}

          {!editingCustomText && (
            <ChildAgeBandsField
              bands={editor.bands}
              typeOptions={props.typeOptions}
              showErrors={editor.showErrors}
              onUpdate={editor.updateBand}
              onRemove={editor.removeBand}
              onAmountUpdate={editor.updateAmount}
              onAmountAdd={editor.addAmount}
              onAmountRemove={editor.removeAmount}
            />
          )}

          {editingCustomText && (
            <>
              <Typography variant="body-medium">
                <strong>Custom Text</strong>
              </Typography>
              <TextField
                size="sm"
                placeholder="Enter custom display text…"
                inputProps={{ "aria-label": "Custom Text" }}
                value={editor.draftCustomText}
                onChange={(e) => editor.setDraftCustomText(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                sx={CUSTOM_TEXT_INPUT_SX}
              />
            </>
          )}
        </Stack>

        {!editingCustomText && (
          <Button
            size="sm"
            variant="tertiary"
            startIcon={<AddIcon />}
            onClick={handleAddBand}
            sx={{ alignSelf: "flex-start", flexShrink: 0, mt: 1 }}
          >
            Add band
          </Button>
        )}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ pt: 1.5, flexShrink: 0 }}
        >
          <Button size="sm" variant="secondary" onClick={editor.handleCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={editor.handleSave}>
            Save
          </Button>
        </Stack>
      </Box>
    );
  },
);
